// src/utils/connectionUtils.js

/**
 * Checks if a flight is an overnight flight
 * @param {Object} flight - Flight data object
 * @returns {boolean} - Whether the flight is overnight
 */
export const isOvernightFlight = (flight) => {
    // Check for the explicit isOvernight flag from backend
    if (flight.isOvernight === true) {
      return true;
    }
    
    // Also check if status contains "Overnight" specifically
    if (flight.status && flight.status.includes("Overnight")) {
      return true;
    }
    
    // Check for "Overnight" string in any value, but be very specific
    return Object.values(flight).some(value => 
      typeof value === 'string' && 
      (value === "Overnight" || value === "Overnight!" || value.endsWith(" Overnight!"))
    );
  };
  
  /**
   * Calculate connection time in minutes, handling overnight flights properly
   * @param {string} outboundETD - Outbound flight departure time (e.g. "15:30")
   * @param {string} inboundETA - Inbound flight arrival time (e.g. "14:15")
   * @param {boolean} isOvernight - Whether this is an overnight flight
   * @returns {number|null} - Connection time in minutes, or null if times invalid
   */
  export const calculateConnectionTime = (outboundETD, inboundETA, isOvernight = false) => {
    // Parse times (assuming format like "15:30")
    const parseTime = (timeStr) => {
      if (!timeStr) return null;
      
      // Handle time formats with 'L' suffix (e.g., "15:30L")
      const cleanTime = timeStr.replace('L', '');
      
      const [hours, minutes] = cleanTime.split(':').map(Number);
      return hours * 60 + minutes; // Convert to minutes
    };
    
    const etaMinutes = parseTime(inboundETA);
    const etdMinutes = parseTime(outboundETD);
    
    if (etaMinutes === null || etdMinutes === null) return null;
    
    // If explicitly marked as overnight, calculate as next day departure
    if (isOvernight) {
      // Add 24 hours (1440 minutes) for next day
      return (etdMinutes + 1440) - etaMinutes;
    }
    
    // Calculate raw difference without handling day boundary
    let diffMinutes = etdMinutes - etaMinutes;
    
    // Handle normal connection time logic
    if (diffMinutes < 0 && diffMinutes > -720) { // Only adjust if difference is less than -12 hours
      // It's a negative connection (departs before arrival)
      return diffMinutes;
    } else if (diffMinutes < 0) {
      // Likely a next-day departure (more than 12 hours before)
      diffMinutes += 24 * 60; // Add 24 hours in minutes
    }
    
    return diffMinutes;
  };
  
  /**
   * Format connection time for display, handling negative times
   * @param {number|null} connectionTime - Connection time in minutes
   * @returns {string} - Formatted connection time string
   */
  export const formatConnectionTime = (connectionTime) => {
    if (connectionTime === null) return 'N/A';
    
    const isNegative = connectionTime < 0;
    const absTime = Math.abs(connectionTime);
    const hours = Math.floor(absTime / 60);
    const minutes = absTime % 60;
    
    // Format with a negative sign for negative connections
    const prefix = isNegative ? '-' : '';
    
    if (hours > 0) {
      return `${prefix}${hours}h ${minutes}m`;
    } else {
      return `${prefix}${minutes}m`;
    }
  };
  
  /**
   * Determine row category based on connection time and parameters
   * @param {number|null} connectionMinutes - Connection time in minutes
   * @param {Object} params - Connection parameters
   * @param {boolean} params.isPriorityFlight - Whether this is a priority flight
   * @param {number} params.hotThreshold - Hot bag threshold in minutes
   * @param {number} params.coldThresholdHours - Cold bag threshold in hours
   * @param {boolean} isOvernight - Whether this is an overnight flight
   * @returns {string} - Category: 'priority', 'hot', 'cold', or 'normal'
   */
  export const getConnectionCategory = (connectionMinutes, params, isOvernight = false) => {
    if (connectionMinutes === null) return 'normal';
    
    const coldThresholdMinutes = params.coldThresholdHours * 60;
    
    // Handle negative connections specially
    if (connectionMinutes < 0) {
      // For priority flights, negative connections are highlighted as priority
      if (params.isPriorityFlight === true) {
        return 'priority'; // Gold highlighting for negative connections on priority flights
      } else {
        return 'hot'; // Red highlighting for negative connections on regular flights
      }
    }
    
    // Handle overnight flights specially if needed
    if (isOvernight) {
      // You could add special styling for overnight flights here if desired
    }
    
    // Regular priority flight logic
    if (params.isPriorityFlight === true && connectionMinutes <= 60) {
      return 'priority'; // Gold highlighting
    }
    
    // Hot bags logic (under threshold minutes) - ONLY if NOT a priority flight
    if (params.isPriorityFlight !== true && connectionMinutes <= params.hotThreshold) {
      return 'hot'; // Red highlighting
    }
    
    // Cold bags logic (over threshold hours)
    if (connectionMinutes >= coldThresholdMinutes) {
      return 'cold'; // Blue highlighting
    }
    
    // Normal connection
    return 'normal';
  };
  
  /**
   * Get styling for a connection category
   * @param {string} category - Category: 'priority', 'hot', 'cold', or 'normal'
   * @returns {Object} - MUI styling object
   */
  export const getCategoryStyle = (category) => {
    const styles = {
      priority: {
        backgroundColor: 'rgba(249, 182, 18, 0.3)', // Gold background with better visibility
        '&:hover': {
          backgroundColor: 'rgba(249, 182, 18, 0.4)',
        }
      },
      hot: {
        backgroundColor: 'rgba(255, 0, 0, 0.2)', // Red background with transparency
        '&:hover': {
          backgroundColor: 'rgba(255, 0, 0, 0.3)',
        }
      },
      cold: {
        backgroundColor: 'rgba(0, 0, 255, 0.2)', // Blue background with transparency
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 255, 0.3)',
        }
      },
      normal: {
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        }
      }
    };
    
    return styles[category] || styles.normal;
  };
  
  /**
   * Helper function to convert hex color to RGB for rgba usage
   * @param {string} hexColor - Hex color string (e.g. "#F9B612")
   * @returns {string} - RGB components as string (e.g. "249, 182, 18")
   */
  export const colorToRGB = (hexColor) => {
    // Remove # if present
    hexColor = hexColor.replace('#', '');
    
    // Parse hex values
    const r = parseInt(hexColor.substring(0, 2), 16);
    const g = parseInt(hexColor.substring(2, 4), 16);
    const b = parseInt(hexColor.substring(4, 6), 16);
    
    return `${r}, ${g}, ${b}`;
  };